import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Clock, ListChecks, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { CheckInDialog } from '@/components/pos/check-in-dialog';
import { RegisterSessions } from '@/components/pos/register-sessions';
import { EmptyState, ReadOnlyNotice } from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth';
import {
  countServedToday,
  listQueue,
  QUEUE_LANES,
  type QueueEntry,
  setQueueStatus,
  WAIT_WARNING_MINUTES,
  waitedMinutes,
} from '@/lib/pos';
import { supabase } from '@/lib/supabase';

/**
 * The check-in queue.
 *
 * Three lanes, because that is the shape of a dispensary floor: waiting at
 * the door, shopping with a budtender, ringing up. Everything else -- served,
 * no-show, cancelled -- is an outcome, and outcomes leave the board.
 *
 * Subscribed to `queue_entries` so a second terminal at the counter sees the
 * same floor: the front desk checks someone in and they appear here without
 * anybody pressing refresh.
 */
export const Route = createFileRoute('/queue/')({
  component: QueuePage,
});

function QueuePage() {
  const { activeAccountId, can } = useAuth();
  const accountId = activeAccountId ?? '';
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const canServe = can('orders.manage');

  const [checkingIn, setCheckingIn] = useState(false);
  // Re-renders the wait badges; the data itself has not changed.
  const [, setTick] = useState(0);

  const entries = useQuery({
    queryKey: ['queue', accountId],
    queryFn: () => listQueue(accountId),
    enabled: Boolean(accountId),
  });

  const served = useQuery({
    queryKey: ['queue-served', accountId],
    queryFn: () => countServedToday(accountId),
    enabled: Boolean(accountId),
  });

  useEffect(() => {
    const timer = setInterval(() => setTick((count) => count + 1), 30_000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!accountId) return;

    const channel = supabase
      .channel(`queue:${accountId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'queue_entries',
          filter: `account_id=eq.${accountId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['queue', accountId] });
          queryClient.invalidateQueries({ queryKey: ['queue-served', accountId] });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [accountId, queryClient]);

  const move = useMutation({
    mutationFn: ({ entry, status }: { entry: QueueEntry; status: QueueEntry['status'] }) =>
      setQueueStatus(accountId, entry.id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['queue', accountId] }),
    onError: (error: Error) => toast.error(error.message),
  });

  const lanes = QUEUE_LANES.map((lane) => ({
    ...lane,
    entries: (entries.data ?? []).filter((entry) => entry.status === lane.status),
  }));

  const waiting = lanes[0]?.entries ?? [];
  const longestWait = waiting.reduce(
    (longest, entry) => Math.max(longest, waitedMinutes(entry)),
    0,
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Queue</h1>
          <p className="text-muted-foreground text-sm">
            {entries.isLoading ? 'Loading…' : `${entries.data?.length ?? 0} on the floor`}
          </p>
        </div>

        {canServe && (
          <Button type="button" onClick={() => setCheckingIn(true)}>
            <UserPlus className="size-4" />
            Check in
          </Button>
        )}
      </div>

      {!canServe && <ReadOnlyNotice permission="orders.manage" />}

      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Waiting" value={String(waiting.length)} />
        <Stat
          label="Longest wait"
          value={`${longestWait} min`}
          warn={longestWait >= WAIT_WARNING_MINUTES}
        />
        <Stat label="Served today" value={String(served.data ?? 0)} />
      </div>

      {entries.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          {lanes.map((lane) => (
            <section key={lane.status} className="bg-muted/30 rounded-lg border p-3">
              <header className="mb-3 flex items-baseline justify-between gap-2">
                <h2 className="text-sm font-semibold">{lane.label}</h2>
                <span className="text-muted-foreground text-xs">{lane.entries.length}</span>
              </header>

              <div className="flex flex-col gap-2">
                {lane.entries.map((entry) => (
                  <article key={entry.id} className="bg-background rounded-md border p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">
                          {entry.customer?.name ?? 'Walk-in'}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {entry.partySize > 1 ? `Party of ${entry.partySize} · ` : ''}
                          {entry.customer?.customerType === 'recreational'
                            ? 'Recreational'
                            : 'Medical'}
                        </p>
                      </div>

                      <WaitBadge entry={entry} />
                    </div>

                    {entry.notes && (
                      <p className="text-muted-foreground mt-2 text-xs italic">{entry.notes}</p>
                    )}

                    {canServe && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {entry.status === 'waiting' && (
                          <>
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => move.mutate({ entry, status: 'shopping' })}
                            >
                              Allow to shop
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => move.mutate({ entry, status: 'no_show' })}
                            >
                              No show
                            </Button>
                          </>
                        )}

                        {entry.status === 'shopping' && (
                          <Button
                            type="button"
                            size="sm"
                            onClick={async () => {
                              await move.mutateAsync({ entry, status: 'checkout' });
                              navigate({
                                to: '/queue/register/$entryId',
                                params: { entryId: entry.id },
                              });
                            }}
                          >
                            Start sale
                          </Button>
                        )}

                        {entry.status === 'checkout' && (
                          <Button
                            type="button"
                            size="sm"
                            onClick={() =>
                              navigate({
                                to: '/queue/register/$entryId',
                                params: { entryId: entry.id },
                              })
                            }
                          >
                            Open register
                          </Button>
                        )}

                        {entry.status !== 'waiting' && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => move.mutate({ entry, status: 'canceled' })}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    )}
                  </article>
                ))}

                {!lane.entries.length && (
                  <p className="text-muted-foreground px-1 py-6 text-center text-xs">{lane.hint}</p>
                )}
              </div>
            </section>
          ))}
        </div>
      )}

      {!entries.isLoading && !entries.data?.length && (
        <EmptyState>
          <ListChecks className="mx-auto mb-2 size-6" />
          Nobody is in the shop right now.
        </EmptyState>
      )}

      <RegisterSessions accountId={accountId} />

      <CheckInDialog
        accountId={accountId}
        open={checkingIn}
        onOpenChange={setCheckingIn}
        onCheckedIn={() => queryClient.invalidateQueries({ queryKey: ['queue', accountId] })}
      />
    </div>
  );
}

function Stat({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="rounded-lg border p-4">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums ${warn ? 'text-destructive' : ''}`}>
        {value}
      </p>
    </div>
  );
}

function WaitBadge({ entry }: { entry: QueueEntry }) {
  const minutes = waitedMinutes(entry);
  const late = minutes >= WAIT_WARNING_MINUTES;

  return (
    <Badge variant={late ? 'destructive' : 'secondary'} className="shrink-0">
      <Clock className="mr-1 size-3" />
      {minutes}m
    </Badge>
  );
}
